import { ChangeEvent, FormEvent, useCallback, useContext, useEffect, useState } from 'react';
import { ITariffInfoDTO } from '../../../interface/TariffInterface';
import { endpoints } from '../../../utils/URL';
import axios from 'axios';
import { addToast } from '../../../utils/toastNotifications';
import { IAppState } from '../../../interface/AppInterface';
import { AppContext } from '../../../context/AppContext';
import { NairaSymbol } from '../../../utils/mappings';
import { ConfirmationDialog } from '../../common/ConfirmationDialog';
import { isFormValidated } from '../../../utils/formUtils';
import ButtonLoader from '../../common/ButtonLoader';

const TariffPage = () => {
  const [tariffs, setTariffs] = useState<ITariffInfoDTO[]>([]);
  const { appState }: { appState: IAppState } = useContext(AppContext);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const getTariffInfo = useCallback(async () => {
    try {
      const url = `${endpoints.tariff.mainUrl}/${appState.pharmacyCode}`;
      const response = await axios.get(url);

      setTariffs(response.data as ITariffInfoDTO[]);
    } catch (error) {
      addToast('Failed to load tariff', 'error');
    }
  }, [appState.pharmacyCode]);

  const handleChange = (tariffId: number) => (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    //find the matching tariff
    const existingTariff = tariffs.find((x) => x.tariffId === tariffId);

    //replace the value
    if (existingTariff) {
      const parsedValue = parseFloat(value);

      //to update the properties of an object in the `models` array using an id,
      setTariffs((prevState) =>
        prevState.map((tariff) =>
          tariff.tariffId === tariffId
            ? { ...tariff, drugCost: parsedValue, isChanged: true }
            : tariff
        )
      );
    }
  };

  const trySaveChanges = (event: FormEvent) => {
    if (!isFormValidated('tariff-form')) {
      return;
    }
    event.preventDefault();

    const isFormEditable = tariffs.filter((x) => x.allowEdit).length > 0 ? true : false;

    if (!isFormEditable) {
      addToast('Tariff cannot be updated at this time', 'error');
      return;
    }

    event.preventDefault();
    setModalOpen(true);
  };

  const saveChangesAsync = async () => {
    //call API

    const url = `${endpoints.tariff.mainUrl}/bulk`;
    setLoading(true);
    try {
      await axios.put(url, tariffs);
      addToast('Tariff Updated success', 'success');
    } catch (error) {
      console.error(error);
      addToast('An error occured while updating', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getTariffInfo();
  }, [getTariffInfo]);

  return (
    <div className='col-lg-10 p-4 content-section my-5'>
      {
        <ConfirmationDialog
          message='Please confirm that you want to save changes to this tariff. Note that changes cannot be made until 1st of next month'
          title='Confirm'
          okText='Ok'
          onConfirm={saveChangesAsync}
          showButtons={true}
          open={modalOpen}
          close={() => setModalOpen(!modalOpen)}
        />
      }
      <div className='d-flex justify-content-between align-item-center'>
        <h4>Tariff Page</h4>
      </div>
      <form className='mt-4' id='tariff-form'>
        <fieldset disabled={loading}>
          <div className='d-flex justify-content-end'>
            <button onClick={trySaveChanges} className='btn btn btn-success px-5'>
              Save all
              {loading && <ButtonLoader />}
            </button>
          </div>
          <div className='mt-2 table-responsive'>
            <table className='table table-bordered'>
              <thead>
                <tr>
                  <th style={{ width: '5%' }}>ID</th>
                  <th style={{ width: '75%' }}>Drug Name</th>
                  <th style={{ width: '20%' }}>Cost ({NairaSymbol})</th>
                </tr>
              </thead>
              <tbody>
                {tariffs.map((tariff, key) => (
                  <tr key={tariff.tariffId}>
                    <td>{key}</td>
                    <td>{tariff.drugName}</td>
                    <td className={tariff.isChanged ? ` border-success border-2` : ''}>
                      <input
                        onChange={handleChange(tariff.tariffId)}
                        value={tariff.drugCost}
                        className='form-control'
                        disabled={tariff.allowEdit}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className='d-flex justify-content-end'>
            <button onClick={trySaveChanges} className='btn btn btn-success px-5'>
              Save all
              {loading && <ButtonLoader />}
            </button>
          </div>
        </fieldset>
      </form>
    </div>
  );
};

export default TariffPage;
